import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { handleFetchResponse } from 'api/errors'
import { Seo } from 'components'
import ModalForm from 'components/ModalForm/ModalForm'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { Form } from 'forms/Form'
import { useActions, useTypedSelector } from 'hooks'
import { useUserInfoQuery } from 'modules/login/api'
import { useCreateOrderMutation } from 'modules/order/api'
import { DEFAULT_CARGO_ITEM, DEFAULT_CARGO_TOTAL_ITEM } from 'modules/order/components/OrderCreate/constants'
import { OrderCreateAgreeOffer } from 'modules/order/components/OrderCreate/OrderCreateAgreeOffer'
import { decode, getPointPayload, prepareCargoField, prepareOrderCreateRequest } from 'modules/order/helpers/order'
import { TOrder } from 'modules/order/types'
import { TOrderCreateCargoForm, TOrderCreateFormData } from 'modules/order/types/orderCreateTypes'
import { OrderCreateCargo, OrderCreateCustomer, OrderCreateRequest, OrderItemClient } from 'schema'
import { changeFields } from 'shared/forms/mutators'

import { USER_INFO_NODE_ID } from '../../components/ModalUserInfo/ModalUserInfo'
import { orderCreateRoute, orderDetailRoute, orderRoute } from '../../routes'
import { decorators, resetTerminalSyncStates } from './decorators'
import OrderCreateForm from './OrderCreateForm'

const DEFAULT_INITIAL_VALUES: TOrderCreateFormData = {
  customer: {
    type: OrderItemClient.type.LEGAL
  },
  departurePoint: undefined,
  destinationPoint: undefined,
  departureDate: undefined,
  cargoType: OrderCreateRequest.cargoType.SEPARATE,
  // @ts-ignore TODO: нужно сделать правильную типизацию формы
  cargo: [DEFAULT_CARGO_ITEM],
  // @ts-ignore TODO: нужно сделать правильную типизацию формы
  cargoTotal: DEFAULT_CARGO_TOTAL_ITEM,
  departurePartner: undefined,
  destinationPartner: undefined,
  payer: undefined,
  comment: undefined,
  operation: undefined
}
const OrderCreateRoute: FC = () => {
  const isFirstRender = useRef(true)
  const [isCompleteUserName, setIsCompleteUserName] = useState(true)
  const [isCompleteUserDocument, setIsCompleteUserDocument] = useState(true)
  const [isShowUserInfoError, setIsShowUserInfoError] = useState(false)

  const { fromPoint, toPoint } = useTypedSelector((state) => state.orderAddReducer)

  const navigate = useNavigate()
  const searchParams = orderCreateRoute.useSearch()
  const formData = searchParams.data
  const { closeModal } = useModal()
  const { setFullInfo, clearOrderAddStore } = useActions()
  const [createOrder, { isLoading }] = useCreateOrderMutation()
  const {
    data: user,
    isLoading: isLoadingUserInfo,
    isFetching: isFetchingUserInfo,
    refetch: refetchUser
  } = useUserInfoQuery(undefined, { refetchOnMountOrArgChange: true })
  const isCompleteUser = Boolean(user?.firstname && user?.lastname)

  const initialValues = useMemo(() => {
    if (formData) {
      const { departure, destination, ...rest } = decode(formData)

      const cargo: Partial<OrderCreateCargo> = {
        amount: rest.amount,
        type: rest.type,
        weight: rest.weight,
        length: rest.length,
        width: rest.width,
        height: rest.height
      }
      const cargoItem = prepareCargoField(cargo) as TOrderCreateCargoForm

      return {
        ...DEFAULT_INITIAL_VALUES,
        cargoType: OrderCreateRequest.cargoType.SEPARATE,
        cargo: [cargoItem],
        departurePoint: {
          addressDetail: { address: departure }
        },
        destinationPoint: {
          addressDetail: { address: destination }
        }
      } as unknown as TOrderCreateFormData
    }

    return DEFAULT_INITIAL_VALUES
  }, [formData])

  useEffect(() => {
    if (!formData) {
      return
    }

    const { departurePoint, destinationPoint } = initialValues

    setFullInfo(
      getPointPayload({
        departurePoint,
        destinationPoint
      })
    )

    return () => {
      clearOrderAddStore()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => () => resetTerminalSyncStates(), [])

  const handleSuccess = useCallback(
    (response: { data: { order: TOrder } }) => {
      const { order } = response.data

      closeModal(MODAL_IDS.CreateOrderForm)
      navigate({ to: orderDetailRoute.to, params: { id: order.id } })
    },
    [closeModal, navigate]
  )

  const scrollToUserInfo = useCallback(() => {
    const containerNode = document.querySelector(`#${USER_INFO_NODE_ID}`)
    containerNode?.scrollIntoView()
  }, [])

  const handleSubmit = useCallback(
    async (data: TOrderCreateFormData) => {
      const { data: userRefetch } = await refetchUser()
      const isCompleteUserName = Boolean(userRefetch?.firstname && userRefetch?.lastname)
      const isCompleteUserDocument =
        Boolean(userRefetch?.document?.length) || data.customer?.type === OrderCreateCustomer.type.LEGAL
      const isCompleteUserRefetch = isCompleteUserName && isCompleteUserDocument

      if (isCompleteUserRefetch) {
        return createOrder(prepareOrderCreateRequest(data)).then(handleFetchResponse(handleSuccess))
      }

      setIsShowUserInfoError(true)
      setIsCompleteUserName(isCompleteUserName)
      setIsCompleteUserDocument(isCompleteUserDocument)
      setTimeout(scrollToUserInfo)
    },
    [createOrder, handleSuccess, refetchUser, scrollToUserInfo]
  )

  const handleClose = useCallback(() => {
    navigate(orderRoute)
    closeModal(MODAL_IDS.CreateOrderForm)
  }, [closeModal, navigate])

  // TODO это костыль тк selectAddress не имеет валидации значения и проброса ошибки в форму
  const submitDisable = useMemo(() => Boolean(toPoint.errorAddress || fromPoint.errorAddress), [toPoint, fromPoint])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false

      return
    }

    if (!(isLoadingUserInfo || isFetchingUserInfo) && !isCompleteUser) {
      setIsShowUserInfoError(true)
    }
  }, [closeModal, isCompleteUser, isFetchingUserInfo, isLoadingUserInfo])

  return (
    <>
      <Seo
        description="Создание новой заявки для размещения в сервисе доставки сборных грузов QP5 Express. Заполнение данных о грузе и предварительный расчет стоимости грузоперевозки."
        title="Новая заявка – сборные перевозки QP5 Express"
      />

      <ModalItem isOpen id={MODAL_IDS.CreateOrderForm}>
        <Form
          keepDirtyOnReinitialize
          decorators={decorators}
          initialValues={initialValues}
          mutators={{ changeFields }}
          name="createOrderForm"
          onSubmit={handleSubmit}
          render={(formProps) => (
            <form name="createOrderForm" onSubmit={formProps.handleSubmit}>
              <ModalForm
                isStickyFooter
                isStickyHeader
                buttonLabel="Заказать"
                footerBefore={<OrderCreateAgreeOffer />}
                footerClassName="flex"
                id={MODAL_IDS.CreateOrderForm}
                isDisableSubmit={submitDisable}
                isLoading={isLoading}
                onClose={handleClose}
                size="xl"
                title="Оформление заявки"
              >
                <OrderCreateForm
                  isCompleteUserDocument={isCompleteUserDocument}
                  isCompleteUserName={isCompleteUserName}
                  isShowUserInfoError={isShowUserInfoError}
                  user={user}
                />
              </ModalForm>
            </form>
          )}
        />
      </ModalItem>
    </>
  )
}

export default memo(OrderCreateRoute)
